import React, { useState, useEffect } from 'react';
import { memberService } from '../../services/member.service';
import { Member } from '../../types';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Loading } from '../../components/ui/Loading';
import { formatDate } from '../../utils/formatters';
import { toast } from 'react-toastify';

export const MembersPage: React.FC = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    membershipType: '',
    membershipStatus: ''
  });

  useEffect(() => {
    loadMembers();
  }, [filters]);

  const loadMembers = async () => {
    try {
      setIsLoading(true);
      const response = await memberService.getAllMembers(filters);
      setMembers(response.items);
    } catch (error: any) {
      toast.error('Failed to load members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewMember = async (id: string) => {
    try {
      const member = await memberService.getMemberById(id);
      setSelectedMember(member);
      setIsModalOpen(true);
    } catch (error) {
      toast.error('Failed to load member details');
    }
  };

  const handleUpdateStatus = async (id: string, status: string) => {
    try {
      await memberService.updateMember(id, { membershipStatus: status as 'ACTIVE' | 'EXPIRED' | 'SUSPENDED' });
      toast.success('Member status updated');
      loadMembers();
    } catch (error) {
      toast.error('Failed to update member status');
    }
  };

  const handleDeleteMember = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this member? This action cannot be undone.')) {
      try {
        await memberService.deleteMember(id);
        toast.success('Member deleted successfully');
        loadMembers();
      } catch (error) {
        toast.error('Failed to delete member');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      EXPIRED: 'bg-red-100 text-red-800',
      SUSPENDED: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loading size="lg" text="Loading members..." />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Member Management</h1>
        <p className="text-gray-600">Manage and view all members</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            placeholder="Search by name, email, or membership ID"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Select
            options={[
              { value: '', label: 'All Types' },
              { value: 'INDIVIDUAL', label: 'Individual' },
              { value: 'FAMILY', label: 'Family' },
              { value: 'CORPORATE', label: 'Corporate' }
            ]}
            value={filters.membershipType}
            onChange={(e) => setFilters({ ...filters, membershipType: e.target.value })}
          />
          <Select
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'ACTIVE', label: 'Active' },
              { value: 'EXPIRED', label: 'Expired' },
              { value: 'SUSPENDED', label: 'Suspended' }
            ]}
            value={filters.membershipStatus}
            onChange={(e) => setFilters({ ...filters, membershipStatus: e.target.value })}
          />
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valid Until</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.map((member) => (
                <tr key={member.id}>
                  <td className="px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900">{member.name}</div>
                      <div className="text-sm text-gray-500">{member.membershipId}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{member.membershipType}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(member.membershipStatus)}`}>
                      {member.membershipStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{formatDate(member.validUntil)}</td>
                  <td className="px-6 py-4 text-sm space-x-2">
                    <Button size="sm" onClick={() => handleViewMember(member.id)}>View</Button>
                    {member.membershipStatus === 'ACTIVE' && (
                      <Button size="sm" variant="danger" onClick={() => handleUpdateStatus(member.id, 'SUSPENDED')}>
                        Suspend
                      </Button>
                    )}
                    {member.membershipStatus === 'SUSPENDED' && (
                      <Button size="sm" variant="success" onClick={() => handleUpdateStatus(member.id, 'ACTIVE')}>
                        Activate
                      </Button>
                    )}
                    <Button size="sm" variant="danger" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDeleteMember(member.id)}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Member Detail Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Member Details" size="lg">
        {selectedMember && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-gray-900">{selectedMember.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Membership ID</label>
                <p className="text-gray-900">{selectedMember.membershipId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Phone</label>
                <p className="text-gray-900">{selectedMember.phone}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Type</label>
                <p className="text-gray-900">{selectedMember.membershipType}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="text-gray-900">{selectedMember.membershipStatus}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Valid Until</label>
                <p className="text-gray-900">{formatDate(selectedMember.validUntil)}</p>
              </div>
            </div>
            {selectedMember.familyMembers && selectedMember.familyMembers.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Family Members</h4>
                <div className="space-y-2">
                  {selectedMember.familyMembers.map((fm) => (
                    <div key={fm.id} className="p-2 bg-gray-50 rounded">
                      <p className="font-medium">{fm.name}</p>
                      <p className="text-sm text-gray-600">{fm.relationship}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
